import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CalendarPage({ token }) {
  const { language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [tab, setTab] = useState('upcoming');
  const [form, setForm] = useState({ title: '', title_hindi: '', event_type: 'event', date: '', end_date: '', description: '' });

  useEffect(() => { loadData(); }, []);

  const api = async (endpoint, method = 'GET', body = null) => {
    const opts = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
    if (method !== 'GET') { opts.method = method; if (body) opts.body = JSON.stringify(body); }
    const res = await fetch(`${API_URL}${endpoint}`, opts);
    return res.json();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [evts, hols, up] = await Promise.all([
        api('/api/calendar/'),
        api('/api/calendar/holidays'),
        api('/api/calendar/upcoming'),
      ]);
      setEvents(evts.events || []);
      setHolidays(hols.holidays || []);
      setUpcoming(up.events || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const addEvent = async () => {
    await api('/api/calendar/', 'POST', form);
    setShowAdd(false);
    setForm({ title: '', title_hindi: '', event_type: 'event', date: '', end_date: '', description: '' });
    loadData();
  };

  const deleteEvent = async (id) => {
    await api(`/api/calendar/${id}`, 'DELETE');
    loadData();
  };

  const typeColors = {
    holiday: 'bg-red-100 text-red-700 border-red-200',
    exam: 'bg-blue-100 text-blue-700 border-blue-200',
    event: 'bg-green-100 text-green-700 border-green-200',
    meeting: 'bg-purple-100 text-purple-700 border-purple-200',
    pta: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  const typeIcons = { holiday: 'fa-umbrella-beach', exam: 'fa-pen', event: 'fa-calendar-day', meeting: 'fa-users', pta: 'fa-handshake' };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {language === 'hi' ? 'कैलेंडर / School Calendar' : 'School Calendar'}
          </h1>
          <p className="text-sm text-gray-500">{language === 'hi' ? 'Events, छुट्टियाँ, परीक्षाएं, meetings' : 'Events, Holidays, Exams, Meetings'}</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600" data-testid="add-event-btn">
          + {language === 'hi' ? 'Event जोड़ें' : 'Add Event'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit">
        {[
          { id: 'upcoming', label: language === 'hi' ? 'आगामी' : 'Upcoming', icon: 'fa-clock' },
          { id: 'all', label: language === 'hi' ? 'सभी Events' : 'All Events', icon: 'fa-list' },
          { id: 'holidays', label: language === 'hi' ? 'छुट्टियाँ' : 'Holidays', icon: 'fa-umbrella-beach' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition ${tab === t.id ? 'bg-white shadow text-orange-700' : 'text-gray-500'}`}
            data-testid={`tab-${t.id}`}>
            <i className={`fa-solid ${t.icon}`}></i> {t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border shadow-sm text-center">
          <p className="text-2xl font-bold text-blue-600">{events.length}</p>
          <p className="text-xs text-gray-400">{language === 'hi' ? 'कुल Events' : 'Total Events'}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm text-center">
          <p className="text-2xl font-bold text-red-600">{holidays.length}</p>
          <p className="text-xs text-gray-400">{language === 'hi' ? 'छुट्टियाँ' : 'Holidays'}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm text-center">
          <p className="text-2xl font-bold text-green-600">{upcoming.length}</p>
          <p className="text-xs text-gray-400">{language === 'hi' ? 'आगामी' : 'Upcoming'}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm text-center">
          <p className="text-2xl font-bold text-purple-600">{events.filter(e => e.event_type === 'exam').length}</p>
          <p className="text-xs text-gray-400">{language === 'hi' ? 'परीक्षाएं' : 'Exams'}</p>
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-3">
        {(tab === 'upcoming' ? upcoming : tab === 'holidays' ? holidays : events).length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <i className="fa-solid fa-calendar-xmark text-4xl text-gray-300 mb-3 block"></i>
            <p className="text-gray-400 font-medium">{language === 'hi' ? 'कोई event नहीं' : 'No events'}</p>
          </div>
        ) : (tab === 'upcoming' ? upcoming : tab === 'holidays' ? holidays : events).map((evt, i) => (
          <div key={evt.id || i} className="bg-white rounded-xl p-4 border shadow-sm flex items-center justify-between hover:shadow-md transition" data-testid={`event-${i}`}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${typeColors[evt.event_type] || typeColors.event}`}>
                <i className={`fa-solid ${typeIcons[evt.event_type] || 'fa-calendar'}`}></i>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-sm">{language === 'hi' && evt.title_hindi ? evt.title_hindi : evt.title}</h3>
                <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                  <span>{evt.date}</span>
                  {evt.end_date && evt.end_date !== evt.date && <span>→ {evt.end_date}</span>}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${typeColors[evt.event_type] || ''}`}>{evt.event_type}</span>
                </div>
              </div>
            </div>
            <button onClick={() => deleteEvent(evt.id)} className="text-gray-300 hover:text-red-500 transition" data-testid={`delete-event-${i}`}>
              <i className="fa-solid fa-trash text-sm"></i>
            </button>
          </div>
        ))}
      </div>

      {/* Add Event Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full" data-testid="add-event-modal">
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="font-bold">{language === 'hi' ? 'नया Event जोड़ें' : 'Add New Event'}</h2>
              <button onClick={() => setShowAdd(false)} className="text-2xl text-gray-400">&times;</button>
            </div>
            <div className="p-5 space-y-3">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" placeholder="Event Title" data-testid="event-title" />
              <input value={form.title_hindi} onChange={e => setForm({ ...form, title_hindi: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" placeholder="Hindi Title (optional)" />
              <select value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" data-testid="event-type">
                <option value="event">Event</option>
                <option value="holiday">Holiday / छुट्टी</option>
                <option value="exam">Exam / परीक्षा</option>
                <option value="meeting">Meeting</option>
                <option value="pta">PTA Meeting</option>
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                  className="px-3 py-2 border rounded-lg" data-testid="event-date" />
                <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                  className="px-3 py-2 border rounded-lg" placeholder="End Date" />
              </div>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg h-20" placeholder="Description (optional)" />
            </div>
            <div className="p-5 border-t flex gap-3">
              <button onClick={() => setShowAdd(false)} className="flex-1 py-2 border rounded-lg text-gray-600">Cancel</button>
              <button onClick={addEvent} disabled={!form.title || !form.date}
                className="flex-1 py-2 bg-orange-500 text-white rounded-lg font-medium disabled:opacity-40" data-testid="save-event-btn">
                {language === 'hi' ? 'Save करें' : 'Save Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
